import type { Declaration, Expression, GlossaProgram, Statement, Subprogram, SwitchCaseValue } from '../parser/ast.js';

/**
 * Όταν μια πραγματική παράμετρος είναι πίνακας, οι διαστάσεις της πρέπει να
 * ταιριάζουν ΑΚΡΙΒΩΣ με τις δηλωμένες διαστάσεις της τυπικής — βλ.
 * `Interpreter.requireMatchingArraySize` για το runtime αντίστοιχο. Αυτός ο
 * έλεγχος τρέχει ΠΡΙΝ την εκτέλεση (ίδια λογική με `findParameterKindMismatch`)
 * ώστε να πιάνει το λάθος ΑΚΟΜΑ ΚΙ αν βρίσκεται σε κλάδο/βρόχο που δεν θα
 * εκτελούνταν σε αυτό το συγκεκριμένο τρέξιμο.
 *
 * "Απόρριψη αντί για μάντεμα": ελέγχεται ΜΟΝΟ όταν ΚΑΙ οι δύο πλευρές έχουν
 * διαστάσεις με γραμματικά literal ακέραιους αριθμούς (π.χ. `Α[10]`) — μια
 * διάσταση μέσω έκφρασης (π.χ. αναφορά σε ΣΤΑΘΕΡΑ) αφήνεται στο runtime
 * check, που μπορεί να την αξιολογήσει.
 */
export interface ArrayParameterSizeMismatch {
  readonly parameterName: string;
  readonly expectedDimensions: readonly number[];
  readonly actualDimensions: readonly number[];
  readonly line: number;
  readonly column: number;
}

function literalDimensions(declarations: readonly Declaration[], name: string): readonly number[] | null {
  for (const decl of declarations) {
    if (decl.kind !== 'VariableDeclaration') continue;
    for (const declarator of decl.declarators) {
      if (declarator.name !== name) continue;
      if (declarator.dimensions.length === 0) return null;
      const dims: number[] = [];
      for (const dim of declarator.dimensions) {
        if (dim.kind !== 'NumberLiteral' || !Number.isInteger(dim.value)) return null;
        dims.push(dim.value);
      }
      return dims;
    }
  }
  return null;
}

function dimensionsEqual(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

function checkCallArguments(
  sub: Subprogram,
  args: readonly Expression[],
  declarations: readonly Declaration[],
): ArrayParameterSizeMismatch | null {
  const count = Math.min(args.length, sub.parameters.length);
  for (let i = 0; i < count; i++) {
    const argExpr = args[i]!;
    if (argExpr.kind !== 'Identifier') continue;
    const actualDims = literalDimensions(declarations, argExpr.name);
    if (actualDims === null) continue;
    const paramName = sub.parameters[i]!;
    const expectedDims = literalDimensions(sub.declarations, paramName);
    if (expectedDims === null) continue;
    if (!dimensionsEqual(actualDims, expectedDims)) {
      return {
        parameterName: paramName,
        expectedDimensions: expectedDims,
        actualDimensions: actualDims,
        line: argExpr.line,
        column: argExpr.column,
      };
    }
  }
  return null;
}

function checkExpr(
  expr: Expression,
  declarations: readonly Declaration[],
  subprograms: ReadonlyMap<string, Subprogram>,
): ArrayParameterSizeMismatch | null {
  switch (expr.kind) {
    case 'NumberLiteral':
    case 'StringLiteral':
    case 'BooleanLiteral':
    case 'Identifier':
      return null;
    case 'ArrayAccess': {
      for (const idx of expr.indices) {
        const result = checkExpr(idx, declarations, subprograms);
        if (result !== null) return result;
      }
      return null;
    }
    case 'FunctionCall': {
      for (const arg of expr.args) {
        const result = checkExpr(arg, declarations, subprograms);
        if (result !== null) return result;
      }
      const sub = subprograms.get(expr.callee.name);
      if (sub === undefined) return null;
      return checkCallArguments(sub, expr.args, declarations);
    }
    case 'UnaryExpression':
      return checkExpr(expr.operand, declarations, subprograms);
    case 'BinaryExpression': {
      const left = checkExpr(expr.left, declarations, subprograms);
      if (left !== null) return left;
      return checkExpr(expr.right, declarations, subprograms);
    }
  }
}

function checkSwitchCaseValue(
  value: SwitchCaseValue,
  declarations: readonly Declaration[],
  subprograms: ReadonlyMap<string, Subprogram>,
): ArrayParameterSizeMismatch | null {
  if (value.kind === 'CaseRange') {
    const from = checkExpr(value.from, declarations, subprograms);
    if (from !== null) return from;
    return checkExpr(value.to, declarations, subprograms);
  }
  if (value.kind === 'CaseComparison') return checkExpr(value.value, declarations, subprograms);
  return checkExpr(value, declarations, subprograms);
}

function checkStatement(
  stmt: Statement,
  declarations: readonly Declaration[],
  subprograms: ReadonlyMap<string, Subprogram>,
): ArrayParameterSizeMismatch | null {
  switch (stmt.kind) {
    case 'AssignmentStatement':
      return checkExpr(stmt.value, declarations, subprograms);
    case 'ReadStatement':
    case 'SwapStatement':
      return null;
    case 'WriteStatement': {
      for (const value of stmt.values) {
        const result = checkExpr(value, declarations, subprograms);
        if (result !== null) return result;
      }
      return null;
    }
    case 'IfStatement': {
      const thenResult = checkStatements(stmt.thenBranch, declarations, subprograms);
      if (thenResult !== null) return thenResult;
      for (const branch of stmt.elseIfBranches) {
        const branchResult = checkStatements(branch.body, declarations, subprograms);
        if (branchResult !== null) return branchResult;
      }
      if (stmt.elseBranch !== null) return checkStatements(stmt.elseBranch, declarations, subprograms);
      return null;
    }
    case 'ForStatement':
      return checkStatements(stmt.body, declarations, subprograms);
    case 'WhileStatement':
      return checkStatements(stmt.body, declarations, subprograms);
    case 'RepeatUntilStatement':
      return checkStatements(stmt.body, declarations, subprograms);
    case 'SwitchStatement': {
      for (const switchCase of stmt.cases) {
        for (const value of switchCase.values) {
          const valueResult = checkSwitchCaseValue(value, declarations, subprograms);
          if (valueResult !== null) return valueResult;
        }
        const bodyResult = checkStatements(switchCase.body, declarations, subprograms);
        if (bodyResult !== null) return bodyResult;
      }
      if (stmt.elseBody !== null) return checkStatements(stmt.elseBody, declarations, subprograms);
      return null;
    }
    case 'CallStatement': {
      for (const arg of stmt.args) {
        const result = checkExpr(arg, declarations, subprograms);
        if (result !== null) return result;
      }
      const sub = subprograms.get(stmt.callee.name);
      if (sub === undefined) return null;
      return checkCallArguments(sub, stmt.args, declarations);
    }
  }
}

function checkStatements(
  statements: readonly Statement[],
  declarations: readonly Declaration[],
  subprograms: ReadonlyMap<string, Subprogram>,
): ArrayParameterSizeMismatch | null {
  for (const stmt of statements) {
    const result = checkStatement(stmt, declarations, subprograms);
    if (result !== null) return result;
  }
  return null;
}

/**
 * Επιστρέφει την ΠΡΩΤΗ ασυμφωνία μεγέθους πίνακα-παραμέτρου (με σειρά
 * εμφάνισης — κύριο πρόγραμμα πρώτα, μετά κάθε υποπρόγραμμα), ή `null` αν
 * δεν υπάρχει καμία. Κάθε σημείο κλήσης ελέγχεται με τις δηλώσεις του ΔΙΚΟΥ
 * ΤΟΥ scope (κύριο πρόγραμμα ή το υποπρόγραμμα μέσα στο οποίο βρίσκεται η
 * κλήση).
 */
export function findArrayParameterSizeMismatch(program: GlossaProgram): ArrayParameterSizeMismatch | null {
  const subprograms = new Map<string, Subprogram>();
  for (const sub of program.subprograms) subprograms.set(sub.name, sub);

  const mainResult = checkStatements(program.body, program.declarations, subprograms);
  if (mainResult !== null) return mainResult;

  for (const sub of program.subprograms) {
    const subResult = checkStatements(sub.body, sub.declarations, subprograms);
    if (subResult !== null) return subResult;
  }
  return null;
}
